import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import { Layout } from 'antd';
import Axios from 'axios';
import _ from 'lodash';

import styled from 'styled-components';

import { MODELS_WALL_QUICKFILTER_DEFAULT } from '@configs/WALLS_CONFIG';

import { getContactMainFeatures } from '@libs/NwFeatures';
import { getFolderContacts } from '@libs/NwFolders';
import { deaccentizeandlowerize, matchesTagFilter } from '@libs/NwUtils';

import { useLayoutContext } from '@contexts/LayoutContext';
import { useModelContext } from '@contexts/ModelContext';
import { UserContext } from '@contexts/UserContext';

import { useModelsList, usePlaylistsCount, modelFullName } from '@hooks/API/Contact/UseContactsList';
import { getActiveShowSeasonModelIdSet, useActiveShowSeasons } from '@hooks/API/ShowSeasons/UseShowSeasons';
import { useFolders } from '@hooks/API/UseFolders';

import ContactsFiltersMobileBar from '@components/ContactsSelector/Common/ContactsFiltersMobileBar';
import ContactsFiltersSidebar from '@components/ContactsSelector/Common/ContactsFiltersSidebar';
import ModelsWall from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsWall';
import { getModelInTown } from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsWallUtils';
import { hasFeatureValue } from '@components/Features/FeatureItemFormUtils';
import { convertFiltersToParams } from '@components/Filters/FilterForm/FilterUtils';
import { StyledLayout } from '@components/Gui/StyledComponent';

import { useContactsSelector, useContactsSelectorDispatch } from '../Common/ContactsSelectorContext';

const { Content, Sider } = Layout;

const SiderStyled = styled(Sider)`
    height: calc(100vh - 60px);
    background-color: #fff;
    z-index: 1030;
    box-shadow:
        0 0.125rem 9.375rem rgba(90, 97, 105, 0.1),
        0 0.25rem 0.5rem rgba(90, 97, 105, 0.12),
        0 0.9375rem 1.375rem rgba(90, 97, 105, 0.1),
        0 0.4375rem 2.1875rem rgba(165, 182, 201, 0.1);
    position: fixed;
    padding: 1em;
    overflow: auto;
`;

const SelectorLayout = styled(Layout)`
    margin-left: ${(props) => (props.$size === 'big' ? '360px' : props.$size === 'small' ? '220px' : '300px')};
    margin-left: ${(props) => props.$type === 'compact' && 0};
`;

const WallWrapper = styled.div`
    position: relative;
`;

/**
 * Confronta un singolo valore di feature con il suo quick filter.
 * Il valore può essere quello standard o quello pubblico: il confronto per tipo non cambia.
 */
const matchesFeatureQuickFilter = (feature, filterValue, value) => {
    switch (feature.T) {
        case 'Choice': {
            // GUID delle scelte: il payload delle modelle può avere un case diverso dalla configurazione
            const normalizedValue = String(value).toLowerCase();
            return filterValue.some((choiceID) => String(choiceID).toLowerCase() === normalizedValue);
        }
        case 'Conversion': {
            const numericValue = parseInt(value);
            return !(filterValue[0] > numericValue || filterValue[1] < numericValue);
        }
        case 'Numeric': {
            const numericValue = parseFloat(value);
            return !(filterValue[0] > numericValue || filterValue[1] < numericValue);
        }
        case 'Rating':
            return !(parseInt(value) < filterValue);
        default:
            return true;
    }
};

const ModelsSelector = () => {
    const { selectedModel, onSelectListType } = useModelContext();

    const { selection, context, additional, global } = useContactsSelector();
    const cs_dispatch = useContactsSelectorDispatch();

    const { activeSet, filters, listType, quickFilters } = selection;
    const { area, currentPackage, currentUserID, projectId, scope, selectorAction, selectorActionID } = context;
    const { showOnlyModelsWithBooks, startOnProjectModels } = additional;

    const { featuresList } = global;

    const { data: folders } = useFolders();

    const layoutContext = useLayoutContext();
    const { breakpoints, isMobile } = layoutContext;

    const { data: fullList, isFetching: fetchingItems } = useModelsList();
    const { data: playlistsCount } = usePlaylistsCount();
    const { data: activeShowSeasons } = useActiveShowSeasons();

    const [isLoading, setIsLoading] = useState(false);
    const [projectItems, setProjectItems] = useState([]);
    const [projectShortlists, setProjectShortlists] = useState([]);
    const [shortlistId, setShortlistId] = useState(null);
    const [selectedGroupName, setSelectedGroupName] = useState(null);
    const [itemsList, setItemsList] = useState([]);
    const [modelsListNotFeaturesFiltered, setModelsListNotFeaturesFiltered] = useState([]);
    const { currentUser } = useContext(UserContext);

    const siderWidth = startOnProjectModels ? 360 : breakpoints < 3 ? 200 : 300;
    const layoutSize = startOnProjectModels ? 'big' : breakpoints < 3 ? 'small' : 'medium';

    const setListType = (listtype) => {
        cs_dispatch({
            type: 'ChangeListType',
            listType: listtype,
        });
        if (onSelectListType) {
            onSelectListType(listtype);
        }
    };

    useEffect(() => {
        if (projectId && startOnProjectModels) {
            const getProjectModels = async () => {
                const prjmod = await Axios.get(`/projectmodels/getforproject/${projectId}`);
                const ModelIDs = prjmod.map((pm) => pm.ModelID);
                if (ModelIDs.length > 0) {
                    setListType('project');
                    setProjectItems(ModelIDs);
                } else {
                    setListType('all');
                }
            };
            const getProjectShortlists = async () => {
                const shortlists = await Axios.get(`/packages/shortlistsforproject/${projectId}`);
                setProjectShortlists(shortlists);
            };
            getProjectModels();
            getProjectShortlists();
        }
    }, []); //projectId, startOnProjectModels, scope

    const shortlistsMap = useMemo(() => {
        if (!projectShortlists || projectShortlists.length === 0) {
            return null;
        }

        const map = new Map();
        projectShortlists.forEach((pack) => {
            if (pack.Recipients) {
                pack.Recipients.forEach((recipient) => {
                    if (recipient.Shortlists) {
                        recipient.Shortlists.forEach((shortlist) => {
                            map.set(shortlist.ID, shortlist);
                        });
                    }
                });
            }
        });
        return map;
    }, [projectShortlists]);

    const [filteredIDs, setFilteredIDs] = useState(null);
    const [fetchingWallIds, setFetchingWallIds] = useState(false);

    const fetchWallIds = async () => {
        setFetchingWallIds(true);
        const modelTypeToSearch = area === 'scouting' ? 'Scouting' : 'Booking';
        const convertedFilters = convertFiltersToParams(filters.active, featuresList, currentUserID);
        if (
            convertedFilters.AgeRange &&
            convertedFilters.AgeRange.length === 2 &&
            (convertedFilters.AgeRange[0] !== null || convertedFilters.AgeRange[1] !== null)
        ) {
            convertedFilters.AgeSince = convertedFilters.AgeRange[0];
            convertedFilters.AgeUntil = convertedFilters.AgeRange[1];
            delete convertedFilters.AgeRange;
        }
        const postParams = {
            ...convertedFilters,
            ModelType: modelTypeToSearch,
            Departments:
                convertedFilters.Departments && convertedFilters.Departments.length > 0
                    ? convertedFilters.Departments
                    : currentUser.ActiveContext.activeDepartments,
        };
        const wallids = await Axios.post('/models/wall-id', postParams);
        setFilteredIDs(wallids);
        setFetchingWallIds(false);
    };

    const filterItemsByFolder = () => {
        const folder = folders && folders.find((folder) => folder.ID.toString() === activeSet);
        if (folder) {
            setFilteredIDs(getFolderContacts(folder));
        }
    };

    useEffect(() => {
        if (listType === 'smart-lists' && activeSet && filters && filters.active) {
            fetchWallIds();
        }
        if (listType === 'folders') {
            filterItemsByFolder();
        }
    }, [filters.active, listType, activeSet, folders]);

    useEffect(() => {
        if ((listType === 'smart-lists' && activeSet && filters && filters.active) || listType === 'folders') {
            if (filteredIDs && Array.isArray(filteredIDs)) {
                cs_dispatch({
                    type: 'UpdateSavedWallIDs',
                    savedWallIDs: [...filteredIDs],
                });
            }
        }
    }, [filteredIDs, folders]);

    const [debouncedNameFilter, setDebouncedNameFilter] = useState(quickFilters.nameFilter);
    useDebounce(
        () => {
            if (quickFilters.nameFilter !== debouncedNameFilter) {
                setDebouncedNameFilter(quickFilters.nameFilter);
            }
        },
        300,
        [quickFilters.nameFilter]
    );

    // const [debouncedGlobalNameFilter, setDebouncedGlobalNameFilter] = useState(quickFilters.globalNameFilter);
    // useDebounce(
    //     () => {
    //         if (quickFilters.globalNameFilter !== debouncedGlobalNameFilter) {
    //             setDebouncedGlobalNameFilter(quickFilters.globalNameFilter);
    //         }
    //     },
    //     300,
    //     [quickFilters.globalNameFilter]
    // );

    useEffect(() => {
        if (selectorAction === 'find-similar' && selectorActionID) {
            const defaultQuickFilters = { ...MODELS_WALL_QUICKFILTER_DEFAULT };
            defaultQuickFilters.showFeaturesQuickFilters = true;
            defaultQuickFilters.featuresQuickFilters = {};

            cs_dispatch({
                type: 'ChangeQuickFilters',
                quickFilters: defaultQuickFilters,
            });
        }
    }, [selectorAction, selectorActionID]);

    const isLoadingItems = () => {
        if (fetchingItems || fetchingWallIds) {
            return true;
        }
        return false;
    };

    const sortByName = (list) => {
        return list.sort((a, b) =>
            modelFullName(a).localeCompare(modelFullName(b), 'en', {
                caseFirst: 'upper',
            })
        );
    };

    useEffect(() => {
        if (!isLoadingItems()) {
            let areaFullList = fullList
                ? area === 'scouting'
                    ? fullList.filter((model) => model.ST === 5)
                    : fullList.filter((model) => model.ST === 4)
                : [];
            let templist = [];

            let normalizedGlobalNameFilter = quickFilters.globalNameFilter
                ? deaccentizeandlowerize(quickFilters.globalNameFilter)
                : null;

            if (normalizedGlobalNameFilter) {
                templist = areaFullList.filter((model) => {
                    let result = true;
                    if (model._normalizedName && model._normalizedSurname && model._normalizedFullName) {
                        result =
                            model._normalizedName.startsWith(normalizedGlobalNameFilter) ||
                            (model._normalizedSurname &&
                                model._normalizedSurname.startsWith(normalizedGlobalNameFilter)) ||
                            model._normalizedFullName.startsWith(normalizedGlobalNameFilter);
                    } else {
                        const normalizedItemName = deaccentizeandlowerize(model.N);
                        const normalizedItemSurname = model.S ? deaccentizeandlowerize(model.S) : '';
                        result =
                            normalizedItemName.startsWith(normalizedGlobalNameFilter) ||
                            (normalizedItemSurname && normalizedItemSurname.startsWith(normalizedGlobalNameFilter)) ||
                            (normalizedItemName + ' ' + normalizedItemSurname).startsWith(normalizedGlobalNameFilter);
                    }
                    return result;

                    // const normalizedModelName = deaccentizeandlowerize(model.N);
                    // const normalizedModelSurname = model.S ? deaccentizeandlowerize(model.S) : '';
                    // return (
                    //     normalizedModelName.includes(normalizedGlobalNameFilter) ||
                    //     normalizedModelSurname.includes(normalizedGlobalNameFilter)
                    // );
                });
                templist = _.sortBy(templist, [
                    // First sort by whether first name starts with filter (false comes before true)
                    function (o) {
                        const normalizedModelName = deaccentizeandlowerize(o.N);
                        return normalizedModelName.startsWith(normalizedGlobalNameFilter) ? 0 : 1;
                    },
                    // Then sort by whether last name starts with filter (false comes before true)
                    function (o) {
                        const normalizedModelSurname = o.S ? deaccentizeandlowerize(o.S) : '';
                        return normalizedModelSurname.startsWith(normalizedGlobalNameFilter) ? 0 : 1;
                    },
                    // Then sort by position of filter in full name (earlier position comes first)
                    function (o) {
                        const normalizedFullName = deaccentizeandlowerize(modelFullName(o));
                        const index = normalizedFullName.indexOf(normalizedGlobalNameFilter);
                        return index === -1 ? 999 : index;
                    },
                    // Finally sort alphabetically within each group
                    function (o) {
                        return modelFullName(o).toLowerCase();
                    },
                ]);
                setItemsList([...templist]);
                return;
            } else {
                // filter by listType
                if (listType === 'folders') {
                    if (filteredIDs && filteredIDs.length) {
                        const filteredItems = areaFullList.filter((item) => filteredIDs.includes(item.ID));
                        templist = filteredIDs
                            .map((id) => filteredItems.find((item) => item.ID === id))
                            .filter((item) => item !== undefined);
                    } else {
                        templist = [];
                    }
                } else {
                    if (listType === 'project') {
                        if (shortlistId) {
                            const shortlist = shortlistsMap && shortlistsMap.get(shortlistId);
                            if (shortlist) {
                                // Filtra gli items della shortlist per gruppo se è selezionato un gruppo
                                let filteredItems = shortlist.Items;
                                if (selectedGroupName) {
                                    filteredItems = shortlist.Items.filter((item) => item.Group === selectedGroupName);
                                }
                                const shortlistModelIDs = filteredItems.map((item) => item.ModelID);
                                templist = areaFullList.filter((model) => shortlistModelIDs.includes(model.ID));
                            } else {
                                templist = [];
                            }
                        } else {
                            templist = areaFullList.filter((model) => projectItems.includes(model.ID));
                        }
                    } else {
                        if (listType === 'all') {
                            const activeDepartments = currentUser.ActiveContext.activeDepartments;
                            templist = areaFullList.filter((model) =>
                                model.DEPS.some((departmentId) => activeDepartments.includes(departmentId))
                            );
                        }
                        if (listType === 'smart-lists') {
                            if (!filteredIDs) {
                                templist = areaFullList;
                            } else {
                                if (filteredIDs.length) {
                                    templist = areaFullList.filter((item) => filteredIDs.includes(item.ID));
                                } else {
                                    templist = [];
                                }
                            }
                        }
                    }
                }

                if (scope === 'package' || scope === 'event' || scope === 'mailing-list' || scope === 'show-season') {
                    templist = templist.filter((model) => model.A);
                }

                //if it's "find similar models", remove the current model from the list
                if (selectorAction === 'find-similar' && selectorActionID) {
                    templist = templist.filter((el) => el.ID !== parseInt(selectorActionID));
                }

                // quick filters (in town, gender, tag filter, name filter - not mobile, only mine)
                if (listType === 'all') {
                    // quick search
                    const { intown, onlymine, tagFilter, tagFilterMode, gender, departmentFilter, showSeasonFilter } =
                        quickFilters;

                    let normalizedNameFilter = debouncedNameFilter ? deaccentizeandlowerize(debouncedNameFilter) : null;
                    templist = templist.filter((item) => {
                        let result = true;
                        if (normalizedNameFilter) {
                            if (item._normalizedName && item._normalizedSurname && item._normalizedFullName) {
                                result =
                                    item._normalizedName.startsWith(normalizedNameFilter) ||
                                    (item._normalizedSurname &&
                                        item._normalizedSurname.startsWith(normalizedNameFilter)) ||
                                    item._normalizedFullName.startsWith(normalizedNameFilter);
                            } else {
                                const normalizedItemName = deaccentizeandlowerize(item.N);
                                const normalizedItemSurname = item.S ? deaccentizeandlowerize(item.S) : '';
                                result =
                                    normalizedItemName.startsWith(normalizedNameFilter) ||
                                    (normalizedItemSurname && normalizedItemSurname.startsWith(normalizedNameFilter)) ||
                                    (normalizedItemName + ' ' + normalizedItemSurname).startsWith(normalizedNameFilter);
                            }
                        }
                        result = result && matchesTagFilter(item, tagFilter, tagFilterMode);
                        return result;
                    });

                    switch (intown) {
                        case 'in':
                            templist = templist.filter((model) => getModelInTown(model.ITS));
                            break;
                        case 'out':
                            templist = templist.filter((model) => !getModelInTown(model.ITS));
                            break;
                        default:
                            break;
                    }

                    switch (gender) {
                        case 'female':
                            templist = templist.filter((model) => model.G === 'Female');
                            break;
                        case 'male':
                            templist = templist.filter((model) => model.G === 'Male');
                            break;
                        case 'non-binary':
                            templist = templist.filter((model) => model.G === 'NonBinary');
                            break;
                        default:
                            break;
                    }

                    if (onlymine) {
                        templist = templist.filter((model) => {
                            if (model.U.find((user) => user.UserID === currentUser.UserID)) {
                                return true;
                            } else {
                                return false;
                            }
                        });
                    }

                    if (departmentFilter) {
                        templist = templist.filter((model) => model.DEPS.includes(departmentFilter));
                    }

                    if (showSeasonFilter) {
                        const modelIdSet = getActiveShowSeasonModelIdSet(showSeasonFilter, activeShowSeasons);
                        if (modelIdSet) {
                            templist = templist.filter((model) => modelIdSet.has(Number(model.ID)));
                        }
                    }
                }

                //if it's package, remove models without books (if the option is selected)
                if (currentPackage && showOnlyModelsWithBooks) {
                    if (playlistsCount && playlistsCount.length) {
                        const modelsWithBooksIDs = playlistsCount.map((pc) => pc.ID);
                        templist = templist.filter((model) => modelsWithBooksIDs.includes(model.ID));
                    } else {
                        templist = [];
                    }
                }

                setModelsListNotFeaturesFiltered([...templist]);
                if (listType === 'all') {
                    // quick filters on features active
                    const { showFeaturesQuickFilters, featuresQuickFilters } = quickFilters;
                    if (showFeaturesQuickFilters) {
                        templist = templist
                            .filter((model) => model.ST)
                            .filter((model) => {
                                const features = getContactMainFeatures(model);
                                let filtered = true;
                                for (const filterKey of Object.keys(featuresQuickFilters)) {
                                    if (featuresQuickFilters[filterKey]) {
                                        const feature = features.find((feature) => feature.ID === filterKey);
                                        if (feature) {
                                            const filterValue = featuresQuickFilters[filterKey];
                                            // la modella resta in lista se rientra nel filtro col valore standard
                                            // oppure, dove c'è, con quello pubblico
                                            const matchesPublicValue =
                                                hasFeatureValue(feature.PV) &&
                                                matchesFeatureQuickFilter(feature, filterValue, feature.PV);
                                            if (
                                                !matchesFeatureQuickFilter(feature, filterValue, feature.V) &&
                                                !matchesPublicValue
                                            ) {
                                                filtered = false;
                                                break;
                                            }
                                        } else {
                                            filtered = false;
                                            break;
                                        }
                                    }
                                }
                                return filtered;
                            });
                    }
                }

                //sorting models
                if (isMobile) {
                    templist = sortByName(templist);
                } else {
                    if (listType === 'folders') {
                        if (quickFilters.sortFolder === 'Name') {
                            templist = sortByName(templist);
                        }
                    } else {
                        switch (quickFilters.sort) {
                            case 'MotherAgency':
                                templist = _.sortBy(templist, [
                                    function (o) {
                                        return o.MAN ? o.MAN.trim().toLowerCase() : 'zzz';
                                    },
                                ]);
                                break;
                            case 'ID':
                                templist = _.sortBy(templist, [
                                    function (o) {
                                        return o.ID;
                                    },
                                ]);
                                break;
                            default:
                                templist = sortByName(templist);
                        }
                    }
                }
                setItemsList([...templist]);
            }
        }
    }, [
        listType,
        fullList,
        filteredIDs,
        activeSet,
        fetchingItems,
        fetchingWallIds,
        quickFilters,
        debouncedNameFilter,
        folders,
        playlistsCount,
        showOnlyModelsWithBooks,
        projectItems,
        shortlistId,
        selectedGroupName,
        activeShowSeasons,
    ]);

    useEffect(() => {
        if (currentPackage) {
            if (itemsList && itemsList.length > 0) {
                if (playlistsCount && playlistsCount.length > 0) {
                    for (const pc of playlistsCount) {
                        const model = itemsList.find((model) => model.ID === pc.ID);
                        if (model) {
                            model.FP = pc.FP;
                            model.CP = pc.CP;
                            model.FAV = pc.FAV;
                            model.PL = pc.PL;
                        }
                    }
                }
            }
        }
    }, [currentPackage, itemsList, playlistsCount]);

    const getWallProps = (sidebar) => {
        const wallProps = {
            inSideBar: sidebar,
            isLoading: isLoading || fetchingWallIds,
            list: itemsList,
            listNotFeaturesFiltered: modelsListNotFeaturesFiltered,
        };
        return wallProps;
    };

    const handleSelectShortlist = (shortlistId, groupName) => {
        setShortlistId(shortlistId);
        setSelectedGroupName(groupName);
    };

    const getFiltersProps = {
        projectModels: projectItems,
        projectShortlists: projectShortlists,
        shortlistId: shortlistId,
        selectedGroupName: selectedGroupName,
        onSelectShortlist: handleSelectShortlist,
        itemType: 'model',
        selectedItem: selectedModel ? true : false,
    };

    if ((!selectedModel && scope !== 'booking') || scope === 'package') {
        return (
            <>
                <StyledLayout>
                    {breakpoints > 2 && (
                        <SiderStyled width={siderWidth}>
                            <ContactsFiltersSidebar {...getFiltersProps} />
                        </SiderStyled>
                    )}
                    <SelectorLayout $size={layoutSize} $type={breakpoints < 3 && 'compact'}>
                        <Content>
                            {breakpoints < 3 && (
                                <ContactsFiltersMobileBar {...getFiltersProps} nItems={itemsList.length} />
                            )}
                            <WallWrapper>
                                <ModelsWall {...getWallProps()} />
                            </WallWrapper>
                        </Content>
                    </SelectorLayout>
                </StyledLayout>
            </>
        );
    } else {
        return (
            <>
                <ContactsFiltersSidebar {...getFiltersProps} />
                <ModelsWall {...getWallProps(true)} />
            </>
        );
    }
};

export default ModelsSelector;
